import _ from 'lodash';
import React, { useCallback, useEffect, useRef } from 'react';
import Button from 'src/@vymo/ui/atoms/button';
import { Card, NoData, TabLayout } from 'src/@vymo/ui/blocks';
import Form from 'src/@vymo/ui/molecules/form';
import { FormVersion } from 'src/@vymo/ui/molecules/form/types';
import { Keys, locale } from 'src/i18n';
import { saveTableParams } from 'src/models/details/slice';
import {
  getRevyreModuleLabelByCode,
  getRevyreWorkflowConfig,
} from 'src/models/lmsConfig/queries';
import { REVYRE_DETAILS } from 'src/modules/constants';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import {
  renderModule,
  setRouteModuleBreadCrumbParams,
} from 'src/workspace/slice';
import { mapInputsToFormValues } from '../form/queries';
import { automatedActivityCategory } from './constants';
import {
  getActivityTabs,
  getEntityDetailsInputsAndGroups,
  getEntityDetailsInputsMap,
  getProfileDetailsInputsGroupsAndValues,
  getReferredEntityDetails,
} from './selectors';
import styles from './index.module.scss';

function DoDetails({ domainType, workflowType, entityId }) {
  const formRef = useRef(null);
  const dispatch = useAppDispatch();
  const { inputs, groups } = useAppSelector((state) =>
    getEntityDetailsInputsAndGroups(state, domainType, workflowType, entityId),
  );
  const {
    inputs: profileInputs,
    groups: profileGroups,
    values: profileValues,
  } = useAppSelector((state) =>
    getProfileDetailsInputsGroupsAndValues(state, entityId),
  );

  const doDetailsInputsMap = useAppSelector((state) =>
    getEntityDetailsInputsMap(state, entityId),
  );
  const referredDoDetails = useAppSelector((state) =>
    getReferredEntityDetails(state, entityId),
  );

  const activityTab = useAppSelector((state) =>
    getActivityTabs(state, { domainType, entityId, workflowType }),
  );

  const doDetailsFormValues = mapInputsToFormValues(doDetailsInputsMap);

  const onEntityClickHandler = useCallback(
    (referredEntityId, entityWorkflowType) => {
      dispatch(
        renderModule({
          id: REVYRE_DETAILS,
          props: {
            domainType: entityWorkflowType,
            entityId: referredEntityId,
          },
        }),
      );
    },
    [dispatch],
  );

  useEffect(() => {
    const typeCode = workflowType || domainType;

    const breadcrumbLabel = getRevyreModuleLabelByCode(typeCode);

    const revyreActiveModuleCodes = getRevyreWorkflowConfig()
      .filter(({ type, display }) => type !== 'HIERARCHY' && display !== false)
      .map(({ code }) => code);

    const shouldHide = !revyreActiveModuleCodes.includes(typeCode);

    if (domainType === 'workflow') {
      dispatch(
        setRouteModuleBreadCrumbParams({
          hidden: shouldHide,
          entityName:
            doDetailsInputsMap?.customerName || doDetailsInputsMap?.id,
          workflowName: _.upperCase(breadcrumbLabel),
        }),
      );
    } else {
      dispatch(
        setRouteModuleBreadCrumbParams({
          hidden: shouldHide,
          entityName:
            doDetailsInputsMap?.customerName ||
            doDetailsInputsMap?.name ||
            doDetailsInputsMap?.id,
          domainName: _.capitalize(breadcrumbLabel),
        }),
      );
    }
  }, [
    dispatch,
    doDetailsInputsMap?.name,
    doDetailsInputsMap?.customerName,
    domainType,
    entityId,
    workflowType,
    doDetailsInputsMap?.id,
  ]);

  const onTabChange = (currentTab: string) => {
    switch (currentTab) {
      case 'automated-activities':
        dispatch(
          saveTableParams({
            filters: {
              activityCategory: JSON.stringify(automatedActivityCategory),
              workflowId: entityId,
            },
          }),
        );
        break;
      case 'activities':
      default:
        dispatch(
          saveTableParams({
            filters: {
              workflowId: entityId,
            },
          }),
        );
        break;
    }
  };

  return (
    <Card
      data-test-id="details-doDetails-form"
      classNames={styles.details__card}
    >
      <TabLayout
        onChange={(currentTab) => onTabChange(currentTab)}
        key={entityId}
        classNames={styles.details__doDetails}
        defaultKey="details"
        items={[
          {
            key: 'details',
            label: locale(Keys.DETAILS),
            order: 0,
            children: (
              <div className={styles.details__doDetails__children}>
                {inputs?.length ? (
                  <Form
                    ref={formRef}
                    id="doDetailsForm"
                    name="doDetailsForm"
                    className={styles.details__doDetails__form}
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    onChange={(formFields) => {}}
                    config={{
                      version: FormVersion.webRevyre,
                      data: [...inputs, ...profileInputs],
                      grouping: [...groups, ...profileGroups],
                      fieldItemConfig: {
                        showDisabledIcon: true,
                      },
                      groupConfig: {
                        collapsibleClasses:
                          styles.details__doDetails__form__collapsible,
                      },
                      viewMode: true,
                    }}
                    value={{ ...doDetailsFormValues, ...profileValues }}
                  />
                ) : (
                  <NoData message={locale(Keys.ENTITY_DETAILS_NOT_FOUND)} />
                )}
              </div>
            ),
          },
          ...referredDoDetails.map((referredEntity, index) => ({
            key: referredEntity?.flowId,
            label: referredEntity?.flowName,
            order: index + 2,
            children: referredEntity?.entities?.length ? (
              <div className={styles.details__doDetails__children}>
                <div className={styles.details__doDetails__children__entities}>
                  {referredEntity?.entities?.map((entity) => (
                    <Button
                      type="link"
                      onClick={() =>
                        onEntityClickHandler(
                          entity?.id,
                          referredEntity?.workflowType,
                        )
                      }
                    >
                      {entity?.name || entity?.id}
                    </Button>
                  ))}
                </div>
              </div>
            ) : (
              <NoData message={locale(Keys.NO_REFERRED_ENTITIES_FOUND)} />
            ),
          })),
          ...activityTab,
        ].sort((a, b) => {
          const orderA = a.order ?? Number.POSITIVE_INFINITY;
          const orderB = b.order ?? Number.POSITIVE_INFINITY;

          return orderA - orderB;
        })}
      />
    </Card>
  );
}

export default DoDetails;
